import { useEffect, useId, useState, useSyncExternalStore } from "react";
import { windowStack, windowTaskbarTextLabel } from "@/app/shell/windowStack";
import { wouldLoseDatasetMappingEdits } from "@/modules/inspect/public";
import { FullscreenWindow, type WindowEntry } from "@/shared/ui/window";
import { wouldLoseDatasetFactsEdits } from "../feature/datasetFactsEditor";
import { DatasetDetailController } from "../feature/datasetDetailController";
import {
  adjacentSectionKey,
  DATASET_SECTION_LABEL,
  DEFAULT_DATASET_SECTION,
  describeDatasetSections,
  type DatasetSection,
} from "../feature/datasetSections";
import { datasetDetailTitleBadge } from "./components/datasetDetailBadge";
import { DatasetDetailActions } from "./components/DatasetDetailActions";
import { DatasetDetailBody } from "./components/DatasetDetailBody";
import { DatasetSectionTabs } from "./components/DatasetSectionTabs";
import type { DatasetDetailWindowParams } from "./datasetDetailWindow";
import styles from "./DatasetDetailWindowContent.module.css";

type DatasetDetailWindowContentProps = {
  entry: WindowEntry;
  /** 스택에서 이 엔트리가 전체화면으로 보이는지. false 면 마운트는 유지하되 창은 접힌다. */
  active: boolean;
};

/**
 * 전체화면 창 스택의 datasetDetail 콘텐츠. route 페이지와 같은 컨트롤러 · 본문을 `FullscreenWindow`
 * 셸에 담는다. 호스트가 entry.id 로 key 를 줘 Dataset 마다 컨트롤러가 새로 생기고, 최소화
 * (`active=false`)에도 mount 가 유지돼 섹션 · 고른 Probe · 모달 상태가 보존된다 — 폴링만 멈춘다.
 */
export function DatasetDetailWindowContent({ active, entry }: DatasetDetailWindowContentProps) {
  const params = entry.params as DatasetDetailWindowParams;
  const idPrefix = useId();
  const [inspectHeaderHost, setInspectHeaderHost] = useState<HTMLDivElement | null>(null);
  const [section, setSection] = useState<DatasetSection>(
    params.initialSection ?? DEFAULT_DATASET_SECTION,
  );
  const [probeUuid, setProbeUuid] = useState<string | null>(params.initialProbeUuid ?? null);
  // 창이 사는 동안 하나다 — 접수로 Probe 가 바뀌면 창 로컬 선택을 옮긴다(route 의 경로 역할).
  const openProbe = (nextProbeUuid: string, nextSection: DatasetSection): void => {
    setProbeUuid(nextProbeUuid);
    setSection(nextSection);
  };
  const [controller] = useState(
    () =>
      new DatasetDetailController({
        datasetUuid: params.datasetUuid,
        onOpenProbe: (nextProbeUuid) => openProbe(nextProbeUuid, "probes"),
      }),
  );

  useEffect(() => {
    void controller.init();
    return () => controller.dispose();
  }, [controller]);

  useEffect(() => {
    controller.setCurrentProbe(probeUuid);
  }, [controller, probeUuid]);

  useEffect(() => {
    controller.setActive(active);
  }, [controller, active]);

  // 이 Dataset 을 잡은 마지막 상세가 닫히면 점검 · 매핑 편집과 조사 사실 편집을 버린다. 그때 잃는 편집이 있으면
  // 스택이 닫기 전에 묻는다. 같은 Dataset 을 route 페이지도 열고 있으면 편집은 그쪽에 남으므로 묻지 않는다.
  useEffect(() => {
    windowStack.setCloseGuard(
      entry.id,
      () =>
        wouldLoseDatasetMappingEdits(controller.datasetUuid) ||
        wouldLoseDatasetFactsEdits(controller.datasetUuid),
    );
    return () => windowStack.setCloseGuard(entry.id, null);
  }, [controller, entry.id]);

  const snapshot = useSyncExternalStore(controller.subscribe, controller.getSnapshot);
  const dataset = snapshot.dataset;
  const title = dataset?.displayName ?? entry.title;

  // 데이터 적재 후 작업표시줄 라벨(제목)을 서버 값으로 보정한다.
  useEffect(() => {
    if (dataset !== null) {
      windowStack.update(entry.id, {
        title: dataset.displayName,
        label: windowTaskbarTextLabel("Dataset:", dataset.displayName),
      });
    }
  }, [entry.id, dataset]);

  const entries = describeDatasetSections(dataset);
  const panelId = `${idPrefix}-panel`;
  const move = (step: 1 | -1) => {
    const next = adjacentSectionKey(entries, section, step);
    if (next !== null) {
      setSection(next);
    }
  };

  return (
    <FullscreenWindow
      minWidth="min(40rem, 100%)"
      open={active}
      title={title}
      titleBadge={datasetDetailTitleBadge(dataset, controller.currentProbe())}
      stepHeader={
        <div className={styles.stepHeader}>
          <DatasetSectionTabs
            entries={entries}
            active={section}
            idPrefix={idPrefix}
            panelId={panelId}
            onSelect={setSection}
          />
          {section === "inspect" ? (
            <div className={styles.inspectHeader} ref={setInspectHeaderHost} data-inspect-header />
          ) : null}
        </div>
      }
      // 탭 줄 양옆의 ←/→ 버튼과 같은 키. 이동 규칙은 탭 줄과 같은 `adjacentSectionKey` 다.
      stepNav={{
        onPrev: () => move(-1),
        onNext: () => move(1),
        prevDisabled: adjacentSectionKey(entries, section, -1) === null,
        nextDisabled: adjacentSectionKey(entries, section, 1) === null,
        prevLabel: "이전 섹션",
        nextLabel: "다음 섹션",
      }}
      headerActions={
        dataset === null ? null : (
          <DatasetDetailActions
            dataset={dataset}
            onCopySourceRef={controller.copySourceRef}
            onHide={controller.openHideDialog}
            onRequestProbe={controller.openRequestProbeDialog}
            onShow={controller.show}
          />
        )
      }
      // 본문이 스크롤을 소유한다 — 셸은 높이만 내준다.
      bodyLayout="fill"
      ariaLabel={`${title} — ${DATASET_SECTION_LABEL[section]}`}
      onMinimize={() => windowStack.minimize(entry.id)}
      onBackdrop={() => windowStack.minimize(entry.id)}
      onRequestClose={() => windowStack.requestClose(entry.id)}
    >
      <DatasetDetailBody
        inspectHeaderHost={inspectHeaderHost}
        active={active}
        controller={controller}
        idPrefix={idPrefix}
        section={section}
        onOpenProbe={openProbe}
      />
    </FullscreenWindow>
  );
}
